import { NavbarProps } from "../types/types";
import Back from "./Back";
import { SidebarTrigger } from "../components/ui/sidebar";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../redux/hooks";
import { logout } from "../redux/auth/authSlice";
import axios from "axios";

const Navbar = ({ showSidebar }: NavbarProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const auth = sessionStorage.getItem("authState");
  const authState = useMemo(() => {
    return auth
      ? JSON.parse(auth)
      : {
          firstName: "Jay",
          lastName: "Hargudson",
          adminLevel: "Account Manager",
        };
  }, [auth]);

  const [staffDetails, setStaffDetails] = useState<any | null>(null);
  const [staffDetailsStatus, setStaffDetailsStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");

  const staffId = authState?.id || authState?.uid;
  const token = authState?.token;
  const hasReferralCode = !!authState?.referralCode;

  const referralCode = useMemo(() => {
    return (
      staffDetails?.referralCode ||
      authState?.referralCode ||
      authState?.officerReferralCode ||
      authState?.staffId ||
      ""
    );
  }, [authState, staffDetails]);

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const [hasCopiedReferral, setHasCopiedReferral] = useState(false);

  useEffect(() => {
    if (!isProfileMenuOpen) return;

    const onPointerDown = (e: MouseEvent) => {
      const container = profileMenuRef.current;
      if (!container) return;
      if (e.target instanceof Node && !container.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsProfileMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (!isProfileMenuOpen) return;
    setHasCopiedReferral(false);
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (!isProfileMenuOpen) return;
    if (staffDetailsStatus === "loading" || staffDetailsStatus === "success")
      return;

    if (!staffId || !token) return;

    if (hasReferralCode) return;

    let isActive = true;
    setStaffDetailsStatus("loading");

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 12_000);
    const authHeader = token.startsWith("Bearer ") ? token : `Bearer ${token}`;

    axios
      .get(`${process.env.REACT_APP_API_URL}/staffs/${staffId}`, {
        headers: { Authorization: authHeader },
        signal: controller.signal,
      })
      .then((res) => {
        if (!isActive) return;
        const data = res?.data?.data;
        setStaffDetails(data || null);
        setStaffDetailsStatus("success");

        if (data && typeof data === "object") {
          try {
            const nextAuthState = {
              ...authState,
              referralCode: data.referralCode ?? authState?.referralCode,
              region: data.region ?? authState?.region,
              email: data.email ?? authState?.email,
              phoneNumber: data.phoneNumber ?? authState?.phoneNumber,
              fullName: data.fullName ?? authState?.fullName,
            };
            sessionStorage.setItem("authState", JSON.stringify(nextAuthState));
          } catch {
            // no-op
          }
        }
      })
      .catch(() => {
        if (!isActive) return;
        setStaffDetailsStatus("error");
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });

    return () => {
      isActive = false;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [
    isProfileMenuOpen,
    staffId,
    token,
    hasReferralCode,
  ]);

  const onLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div className="sticky top-0 z-30 flex h-16 items-center justify-between bg-white/80 backdrop-blur-md px-4 lg:px-6 border-b border-gray-100">
      <style>
        {`
          @keyframes pop {
            0% { transform: scale(0.6); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
      <div className="flex items-center gap-3">
        <SidebarTrigger className="text-gray-500 hover:text-[#008751]" />
        <div className="hidden lg:block">
          <Back />
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* User Profile */}
        <div
          ref={profileMenuRef}
          className="relative flex items-center gap-3 border-l border-gray-100 pl-4"
        >
          <button
            type="button"
            onClick={() => setIsProfileMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={isProfileMenuOpen}
            className="flex items-center gap-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#008751]/30"
          >
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-[#008751] flex items-center justify-center text-white font-bold text-sm">
                {authState.firstName?.[0]}
                {authState.lastName?.[0] || "JH"}
              </div>
            </div>
            <div className="hidden md:block text-left">
              <h4 className="text-sm font-bold text-gray-900 leading-none">
                {authState.firstName} {authState.lastName}
              </h4>
              <p className="text-[11px] font-medium text-gray-400 mt-0.5">
                {authState.adminLevel || authState.role || ""}
              </p>
            </div>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              className="hidden md:block text-gray-400"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isProfileMenuOpen && (
            <div
              role="menu"
              aria-label="Profile menu"
              className="absolute right-0 top-[calc(100%+10px)] w-80 rounded-2xl border border-gray-100 bg-white shadow-lg overflow-hidden"
            >
              <div className="p-4 border-b border-gray-50">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#008751] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {authState.firstName?.[0]}
                    {authState.lastName?.[0] || "JH"}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {staffDetails?.fullName ||
                        `${authState.firstName || ""} ${authState.lastName || ""}`.trim()}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {authState.adminLevel || authState.role || "Account"}
                    </p>
                    {(staffDetails?.email || authState?.email) && (
                      <p className="text-[11px] text-gray-400 truncate mt-1">
                        {staffDetails?.email || authState?.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4 space-y-3">
                <div className="rounded-xl bg-gray-50/60 border border-gray-100 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                        Referral Code
                      </p>
                      <p className="text-sm font-black text-gray-900 mt-1 truncate">
                        {staffDetailsStatus === "loading"
                          ? "Loading..."
                          : staffDetailsStatus === "error"
                            ? "—"
                            : referralCode || "—"}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={!referralCode}
                      onClick={async () => {
                        if (!referralCode) return;
                        try {
                          await navigator.clipboard.writeText(referralCode);
                          setHasCopiedReferral(true);
                          window.setTimeout(() => {
                            setHasCopiedReferral(false);
                          }, 1500);
                        } finally {
                          // keep dropdown open
                        }
                      }}
                      className={`px-3 py-2 rounded-xl text-[11px] font-black border bg-white disabled:opacity-50 transition-colors ${hasCopiedReferral ? "border-[#008751]/30 text-[#008751]" : "border-gray-200 text-gray-600 hover:text-[#008751] hover:border-[#008751]/30"} disabled:hover:text-gray-600 disabled:hover:border-gray-200`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {hasCopiedReferral ? (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="animate-[pop_180ms_ease-out]"
                              aria-hidden="true"
                            >
                              <path
                                d="M20 6L9 17l-5-5"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Copied
                          </>
                        ) : (
                          "Copy"
                        )}
                      </span>
                    </button>
                  </div>
                </div>

                {(authState.region ||
                  authState.staffId ||
                  staffDetails?.phoneNumber ||
                  authState?.phoneNumber) && (
                  <div className="grid grid-cols-2 gap-3">
                    {authState.staffId && (
                      <div className="rounded-xl border border-gray-100 p-3">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          Staff ID
                        </p>
                        <p className="text-xs font-bold text-gray-900 mt-1 truncate">
                          {authState.staffId}
                        </p>
                      </div>
                    )}
                    {(staffDetails?.phoneNumber || authState?.phoneNumber) && (
                      <div className="rounded-xl border border-gray-100 p-3">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          Phone
                        </p>
                        <p className="text-xs font-bold text-gray-900 mt-1 truncate">
                          {staffDetails?.phoneNumber || authState?.phoneNumber}
                        </p>
                      </div>
                    )}
                    {authState.region && (
                      <div className="rounded-xl border border-gray-100 p-3">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                          Region
                        </p>
                        <p className="text-xs font-bold text-gray-900 mt-1 truncate">
                          {authState.region}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-2 border-t border-gray-50">
                <button
                  type="button"
                  role="menuitem"
                  onClick={onLogout}
                  className="w-full text-left px-3 py-2 rounded-xl text-sm font-bold text-red-600 hover:bg-red-50 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Navbar;
